import * as React from "react";
import type {BrowserHistory} from "history";
import {Router} from "react-router-dom";
import {getLanguageBasename} from "@atg-shared/language";
import type {HistoryState} from "../../types";

const languageBasename = getLanguageBasename();

type Props = {
    basename?: string;
    history: BrowserHistory;
    children: React.ReactNode;
    onLocationChange?: (state: HistoryState) => void;
};

/**
 * A wrapper around `Router` from `react-router-dom` that uses a custom history object and a callback that triggers when the history is updated
 */
export function HistoryRouter({
    basename: basenameFromProps = "",
    children,
    history,
    onLocationChange,
}: Props) {
    const basename = `${languageBasename}${basenameFromProps}`;

    const [state, setHistory] = React.useState<HistoryState>({
        action: history.action,
        location: history.location,
    });

    React.useLayoutEffect(
        () =>
            history.listen(() => {
                setHistory({action: history.action, location: history.location});
                if (onLocationChange) {
                    onLocationChange({
                        action: history.action,
                        location: history.location,
                    });
                }
            }),
        [history, onLocationChange, basename],
    );

    return (
        <Router
            basename={basename}
            location={state.location}
            navigator={history}
            navigationType={state.action}
        >
            {children}
        </Router>
    );
}
